<template>
  <article class="article-view">
    <div v-if="article.image" class="image-container">
      <img :src="article.image" :alt="article.name">
    </div>
    <div v-sane-html="article.content_html" class="article-content content" />
  </article>
</template>

<script lang="ts" setup>
  import { ScriptLoader } from '~/shared/utils/script-loader'
  import { YANDEX_FORMS_EMBED_URL } from '~/shared/const/links'
  import type { Article } from '~/models/articles'

  defineProps<{
    article: Article
  }>()

  onMounted(() => {
    ScriptLoader.attach(YANDEX_FORMS_EMBED_URL).catch((e) => {
      if (!ScriptLoader.isErrorLoader(e)) {
        throw e
      }
    })
  })
</script>

<style lang="scss" scoped>
  .article-view {
    @extend %flex-column;

    width: 100%;
    gap: 24px;

    > .image-container {
      width: 100%;

      > img {
        width: 100%;
        height: auto;
        border-radius: 32px;

        @include vp-1279 {
          border-radius: 24px;
        }
      }
    }

    > .content {
      background-color: $color-default-white;
      border-radius: 24px;
      padding: 32px;
      font-size: 16px;
      line-height: 24px;

      @include vp-767 {
        padding: 20px;
        border-radius: 16px;
      }

      :deep(h1) {
        font-size: 40px;
        font-weight: 500;
        line-height: 48px;
        margin: 24px 0;

        @include vp-1279 {
          font-size: 32px;
          line-height: 40px;
        }

        @include vp-767 {
          font-size: 24px;
          line-height: 32px;
        }

        &:first-child {
          margin-top: 0;
        }
      }

      :deep(h2) {
        font-size: 32px;
        font-weight: 500;
        line-height: 40px;
        margin: 24px 0;

        @include vp-767 {
          font-size: 24px;
          line-height: 32px;
        }

        &:first-child {
          margin-top: 0;
        }
      }

      :deep(h3) {
        font-size: 24px;
        font-weight: 500;
        line-height: 32px;
        margin: 20px 0;

        @include vp-767 {
          font-size: 20px;
          line-height: 28px;
        }

        &:first-child {
          margin-top: 0;
        }
      }

      :deep(h4) {
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
        margin: 16px 0;

        @include vp-767 {
          font-size: 18px;
          line-height: 26px;
        }

        &:first-child {
          margin-top: 0;
        }
      }

      :deep(p) {
        margin: 16px 0;

        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(a) {
        color: $color-kiwi-50;
        text-decoration: underline;
      }

      :deep(iframe) {
        width: 100%;
        height: 440px;
        margin: 16px 0;
        border-radius: 32px;

        @include vp-1279 {
          border-radius: 24px;
        }

        @include vp-767 {
          height: 163px;
        }
      }

      :deep(img) {
        width: 100% !important;
        height: auto !important;
        border-radius: 32px;
        margin: 16px 0;

        @include vp-1279 {
          border-radius: 24px;
        }
      }

      :deep(figcaption),
      :deep(.image-description),
      :deep(img + span),
      :deep(img + p) {
        display: inline-block;
        margin-top: -8px;
        font-size: 14px;
        line-height: 22px;
        text-align: center;
        color: $color-potato-60;
        width: 100%;
      }

      :deep(.gallery),
      :deep(figure) {
        @extend %flex-column;

        gap: 24px;
        margin: 24px 0;

        img {
          border-radius: 32px;

          @include vp-1279 {
            border-radius: 24px;
          }
        }
      }

      :deep(ul) {
        list-style: disc inside;
        padding: 16px;
        margin: 16px 0;

        > li {
          font-size: 16px;
          line-height: 24px;
        }

        > li + li {
          margin-top: 12px;
        }
      }

      :deep(ol) {
        list-style-position: inside;
        padding: 16px;
        margin: 16px 0;

        > li {
          font-size: 16px;
          line-height: 24px;
        }

        > li + li {
          margin-top: 12px;
        }
      }

      :deep(blockquote) {
        position: relative;
        margin: 24px 0;
        padding: 16px 16px 16px 52px;
        font-size: 16px;
        line-height: 24px;
        color: $color-potato-90;

        &::before {
          position: absolute;
          content: '';
          top: 16px;
          left: 16px;
          width: 24px;
          height: 24px;
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEzLjA4NyA3LjkwNzI3QzEzLjA4NyA2Ljk2MDYgMTMuNzg3IDYuMTg3MyAxNC44MDcgNi4xODczTDE2Ljk0NyA2LjE4NzNDMTcuODkzNyA2LjE4NzMgMTguNjY3IDYuOTYwNiAxOC42NjcgNy45MDcyN0wxOC42NjcgMTEuNzczOSIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xOC42NjcgMTEuNzY3NUwxNC44MDAzIDExLjc2NzVDMTQuMTYwMyAxMS43Njc1IDEzLjY1MzcgMTEuNDY3NCAxMy4zNjAzIDExLjAwNzQiIHN0cm9rZT0iI0U2QjQwMCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTguNjY3IDExLjc2NzJDMTguNjY3IDE1LjgwMDUgMTcuOTEzNyAxNi40NjcyIDE1LjY0NyAxNy44MTM4IiBzdHJva2U9IiNFNkI0MDAiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTEwLjkxMzMgMTEuNzY3M0w3LjA0NjU5IDExLjc2NzNDNi4wMjY1OSAxMS43NjczIDUuMzI2NTkgMTAuOTk0IDUuMzI2NTkgMTAuMDQ3M0w1LjMyNjU5IDcuOTAwNjNDNS4zMjY1OSA2Ljk1Mzk3IDYuMDI2NTkgNi4xODA2NiA3LjA0NjU5IDYuMTgwNjZMOS4xOTk5MiA2LjE4MDY2QzEwLjE0NjYgNi4xODA2NiAxMC45MTk5IDYuOTUzOTcgMTAuOTE5OSA3LjkwMDYzTDEwLjkxOTkgMTEuNzY3MyIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xMC45MTMxIDExLjc2N0MxMC45MTMxIDE1LjgwMDMgMTAuMTU5OCAxNi40NjY5IDcuODkzMDkgMTcuODEzNiIgc3Ryb2tlPSIjRTZCNDAwIiBzdHJva2Utd2lkdGg9IjEuNjY2NjEiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K');
          background-repeat: no-repeat;
        }
      }

      :deep(table) {
        width: 100%;
        margin: 24px 0;
        border-collapse: collapse;
        border-radius: 12px;
        overflow: hidden;

        @include vp-767 {
          font-size: 14px;
          line-height: 22px;
        }

        th,
        td {
          padding: 12px;
          border: 1px solid $color-potato-20;
          text-align: left;
        }

        th {
          background-color: $main-back-color;
          font-weight: 500;
        }
      }

      :deep(pre) {
        white-space: pre-wrap;
      }

      :deep(hr) {
        border: none;
        height: 1px;
        background-color: $color-potato-20;
        margin: 32px 0;
      }
    }
  }
</style>
